import { Permissions } from '../../authorization'

import { ROTransaction, RWTransaction, runWithROTransaction } from '../../../db'
import {
  BaseDBService,
  BaseDBServiceOpts,
  Context,
  CoreDict,
  Id,
  PaginatedResult,
  createObj,
  enumGetValues,
  runIfDefined,
  getUpdatedString,
  throwAccessDenied,
  throwInvalidOperation,
  throwInvalidParameter,
  throwObjectAlreadyExists,
  throwObjectNotFound,
  trCore,
  validateOptionalNumber,
  validateOptionalPhone,
  validateOptionalString,
  validateRequiredId,
  validateRequiredNumber,
  validateRequiredString,
} from '../../../common'
import { CreateEventMode, ModelEvent, ModelEventCreator, ModelEventsProcessor } from '../../events'
import { MunicipalEntitiesRepository } from '../../municipalEntity'
import { User, UsersRepository } from '../../users'
import {
  AddCardIssuePointOpts,
  CardIssuePoint,
  CardIssuePointFindOptions,
  CardIssuePointsRepository,
  CardIssuePointsService,
  UpdateCardIssuePointOpts,
} from './cardIssuePoints'
import { FullTextSearchService } from '../../fullTextSearch'
import { isEqual } from 'lodash'

enum EventType {
  CARD_ISSUE_POINT_ADD = 'CARD_ISSUE_POINT_ADD',
  CARD_ISSUE_POINT_UPDATE = 'CARD_ISSUE_POINT_UPDATE',
  CARD_ISSUE_POINT_DELETE = 'CARD_ISSUE_POINT_DELETE',
  CARD_ISSUE_POINT_MERGE = 'CARD_ISSUE_POINT_MERGE',
}

type AddCardIssuePointPayload = {
  municipalEntityId: Id
  number: number
  locality: string
  street?: string
  building?: string
  comment?: string
  windowsCount: number
  openingHours?: string
  phone?: string
  responsiblePersonId?: Id
}

type UpdateCardIssuePointPayload = {
  municipalEntityId?: Id
  number?: number
  locality?: string
  street?: string
  building?: string
  comment?: string
  windowsCount?: number
  openingHours?: string
  phone?: string
  responsiblePersonId?: Id | null
}

type MergeCardIssuePointPayload = {
  createdAt: string
  deletedAt?: string
  municipalEntityId: Id
  number: number
  locality: string
  street?: string
  building?: string
  comment?: string
  windowsCount: number
  openingHours?: string
  phone?: string
  responsiblePersonId?: Id
}

interface DBCardIssuePointsServiceOpts extends BaseDBServiceOpts<CoreDict> {
  usersRepository: UsersRepository
  municipalEntitiesRepository: MunicipalEntitiesRepository
  cardIssuePointsRepository: CardIssuePointsRepository
}

export class DBCardIssuePointsService
  extends BaseDBService<DBCardIssuePointsServiceOpts, CoreDict>
  implements CardIssuePointsService
{
  constructor(opts: DBCardIssuePointsServiceOpts) {
    super('DBCardIssuePointsService', opts)
    this.logger.debug({}, '<9bb47fa7> Init')
  }

  private validateUserID = (userID?: string) => validateRequiredId('<94a3cc06> User ID', userID)
  private validateAddNumber = (number?: number) => validateRequiredNumber('<c762ad1e> Number', number, { minValue: 1 })
  private validateUpdateNumber = (number?: number) =>
    validateOptionalNumber('<e0178c03> Number', number, { minValue: 1 })
  private validatePhone = (phone?: string) => validateOptionalPhone('<bd5a17b9> Телефон', phone?.trim())
  private validateAddWindowsCount = (windowsCount: number) =>
    validateRequiredNumber('<e43f479a> Windows count', windowsCount, { minValue: 1 })
  private validateUpdateWindowsCount = (windowsCount?: number) =>
    validateOptionalNumber('<56c6881a> Windows count', windowsCount, { minValue: 1 })
  private validateNewLocality = (locality?: string) => validateRequiredString('<3a558756> Locality', locality?.trim())
  private validateUpdateLocality = (locality?: string) =>
    validateOptionalString('<b51a75eb> Locality', locality?.trim(), { minSize: 1 })

  async findAll(ctx: Context, opts?: CardIssuePointFindOptions | undefined): Promise<PaginatedResult<CardIssuePoint>> {
    await runWithROTransaction(this.opts.dbConnection, async tx => {
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.CARD_ISSUE_POINTS_VIEW))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_CARD_ISSUE_POINTS_VIEW)

        /**
         * * Если модератор/оператор запросил список всех ПВК,
         * * то по умолчанию добавляем в фильтр его МО.
         */
        if (!opts?.filter?.municipalEntityId) {
          const user = await this.opts.usersRepository.findById(tx, this.validateUserID(ctx.userId))
          opts = { ...opts, filter: { ...opts?.filter, municipalEntityId: user?.municipalEntityId } }
        }

        await this.checkAccessToMunicipalEntity('<173de095>', ctx, tx, [opts?.filter?.municipalEntityId])
      }
    })

    return await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.cardIssuePointsRepository.findAll(tx, opts),
    )
  }

  async findById(ctx: Context, id: string): Promise<CardIssuePoint | undefined> {
    return await runWithROTransaction(this.opts.dbConnection, async tx => {
      const cip: CardIssuePoint | undefined = await this.opts.cardIssuePointsRepository.findById(tx, id)
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.CARD_ISSUE_POINTS_VIEW))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_CARD_ISSUE_POINTS_VIEW)

        await this.checkAccessToMunicipalEntity('<268190e3>', ctx, tx, [cip?.municipalEntityId])
      }
      return cip
    })
  }

  async findByIdNonDeleted(ctx: Context, id: string): Promise<CardIssuePoint | undefined> {
    return await runWithROTransaction(this.opts.dbConnection, async tx => {
      const cip: CardIssuePoint | undefined = await this.opts.cardIssuePointsRepository.findById(tx, id)
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.CARD_ISSUE_POINTS_VIEW))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_CARD_ISSUE_POINTS_VIEW)

        await this.checkAccessToMunicipalEntity('<75566004>', ctx, tx, [cip?.municipalEntityId])
      }
      return cip
    })
  }

  async add(ctx: Context, opts: AddCardIssuePointOpts): Promise<Id> {
    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.CARD_ISSUE_POINTS_MANAGE))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_CARD_ISSUE_POINTS_ADD)
        await this.checkAccessToMunicipalEntity('<409aa675>', ctx, tx, [opts.municipalEntityId])
      }

      await this.checkForMunicipalEntityExists(ctx, tx, opts.municipalEntityId)
      if (opts.responsiblePersonId) {
        await this.checkAccessToMunicipalEntity(
          '<4d3602e2>',
          ctx,
          tx,
          [opts.municipalEntityId],
          this.validateUserID(opts.responsiblePersonId),
        )
      }
      await this.checkCardIssuePointUniqueness(ctx, tx, this.validateAddNumber(opts.number), opts.municipalEntityId)

      this.validateAddress(ctx, opts.street?.trim(), opts.building?.trim(), opts.comment?.trim())

      const entityId = this.opts.idGenerator.generate()

      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.NEW,
        entityId,
        type: EventType.CARD_ISSUE_POINT_ADD,
        municipalEntityId: opts.municipalEntityId,
        payload: createObj<AddCardIssuePointPayload>({
          municipalEntityId: opts.municipalEntityId,
          number: opts.number,
          locality: this.validateNewLocality(opts.locality),
          street: opts.street,
          building: opts.building,
          comment: opts.comment,
          windowsCount: this.validateAddWindowsCount(opts.windowsCount),
          openingHours: opts.openingHours,
          phone: this.validatePhone(opts.phone),
          responsiblePersonId: opts.responsiblePersonId,
        }),
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])

    return event.entityId
  }

  async update(ctx: Context, id: string, opts: UpdateCardIssuePointOpts): Promise<void> {
    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.CARD_ISSUE_POINTS_MANAGE))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_CARD_ISSUE_POINTS_UPDATE)

        const cip = await this.opts.cardIssuePointsRepository.findById(tx, id)

        await this.checkAccessToMunicipalEntity('<07685756>', ctx, tx, [opts.municipalEntityId, cip?.municipalEntityId])
      }

      const cip = await this.checkForCardIssuePointExists('<950cfa95>', ctx, tx, id)

      if (opts.municipalEntityId) {
        await this.checkForMunicipalEntityExists(ctx, tx, opts.municipalEntityId)
        await this.checkForCardIssuePointHasOperators(
          '<42b5a4d8>',
          this.translate(ctx, d => d.cardIssuePoints.hasUsersOnMEChange),
          ctx,
          tx,
          id,
        )
        if (opts.responsiblePersonId) {
          await this.checkAccessToMunicipalEntity(
            '<fedf5ff2>',
            ctx,
            tx,
            [opts.municipalEntityId],
            this.validateUserID(opts.responsiblePersonId),
          )
        } else opts.responsiblePersonId = null
      } else {
        if (opts.responsiblePersonId)
          await this.checkAccessToMunicipalEntity(
            '<5680c562>',
            ctx,
            tx,
            [cip.municipalEntityId],
            opts.responsiblePersonId,
          )
      }

      if (
        (opts.number !== undefined && opts.number != cip.number) ||
        (opts.municipalEntityId && opts.municipalEntityId !== cip.municipalEntityId)
      )
        await this.checkCardIssuePointUniqueness(
          ctx,
          tx,
          this.validateUpdateNumber(opts.number) || cip.number,
          opts.municipalEntityId || cip.municipalEntityId,
        )

      this.validateAddress(
        ctx,
        opts.street?.trim() ?? cip.street,
        opts.building?.trim() ?? cip.building,
        opts.comment?.trim() ?? cip.comment,
      )

      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: id,
        type: EventType.CARD_ISSUE_POINT_UPDATE,
        municipalEntityId: cip.municipalEntityId,
        payload: createObj<UpdateCardIssuePointPayload>({
          municipalEntityId: opts.municipalEntityId,
          number: opts.number,
          locality: this.validateUpdateLocality(opts.locality),
          street: opts.street,
          building: opts.building,
          comment: opts.comment,
          windowsCount: this.validateUpdateWindowsCount(opts.windowsCount),
          openingHours: opts.openingHours,
          phone: opts.phone ? this.validatePhone(opts.phone) : opts.phone,
          responsiblePersonId: opts.responsiblePersonId,
        }),
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }

  async delete(ctx: Context, id: string): Promise<void> {
    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.CARD_ISSUE_POINTS_MANAGE))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_CARD_ISSUE_POINTS_DELETE)

        const cip = await this.opts.cardIssuePointsRepository.findById(tx, id)
        await this.checkAccessToMunicipalEntity('<99fb2504>', ctx, tx, [cip?.municipalEntityId])
      }

      const cip = await this.checkForCardIssuePointExists('<731b662e>', ctx, tx, id)
      const users = await this.opts.usersRepository.findAll(tx, { filter: { cardIssuePointId: id } })
      if (users.items.length > 0)
        throwInvalidOperation(
          this.translate(ctx, d => d.cardIssuePoints.hasUsers),
          { uid: '<898d40ff>' },
        )
      await this.checkForCardIssuePointHasOperators(
        '<5c1aacdc>',
        this.translate(ctx, d => d.cardIssuePoints.hasUsers),
        ctx,
        tx,
        id,
      )

      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: id,
        municipalEntityId: cip.municipalEntityId,
        type: EventType.CARD_ISSUE_POINT_DELETE,
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }

  private async checkAccessToMunicipalEntity(
    errorCode: string,
    ctx: Context,
    tx: ROTransaction,
    meIds: (string | undefined)[],
    userID?: Id,
  ): Promise<void> {
    const filteredMEIds = meIds?.filter(id => id != undefined)

    userID ||= this.validateUserID(ctx.userId)

    const user = await this.checkForUserExists(ctx, tx, userID)

    filteredMEIds.forEach(id => {
      if (user.municipalEntityId != id) {
        throwAccessDenied(
          `${errorCode} ${this.opts.translationService.translate(ctx.langCode, d => d.municipalEntities.accessDenied)}`,
        )
      }
    })
  }

  private validateAddress(
    ctx: Context,
    street: string | undefined,
    building: string | undefined,
    comment: string | undefined,
  ): void {
    street = street ?? ''
    building = building ?? ''
    comment = comment ?? ''

    if ((street + building + comment).length == 0 || (comment + street).length == 0 || (comment + building).length == 0)
      throwInvalidParameter(
        this.translate(ctx, d => d.cardIssuePoints.invalidAddress),
        { uid: '<f8c24c66>' },
      )
  }

  private async checkCardIssuePointUniqueness(
    ctx: Context,
    tx: ROTransaction,
    number: number,
    meID: Id,
  ): Promise<void> {
    const cardIssuePoints = await this.opts.cardIssuePointsRepository.findAll(tx, {
      filter: { number: number, municipalEntityId: meID },
    })
    if (cardIssuePoints.items.length != 0)
      throwObjectAlreadyExists(
        this.translate(ctx, d => d.cardIssuePoints.alreadyExists),
        { uid: '<72dea2f0>' },
      )
  }

  private async checkForCardIssuePointHasOperators(
    errorCode: string,
    errorMessage: string,
    ctx: Context,
    tx: ROTransaction,
    id: Id,
  ): Promise<void> {
    const users = await this.opts.usersRepository.findAll(tx, { filter: { cardIssuePointId: id } })
    if (users.items.length > 0) throwInvalidOperation(errorMessage, { uid: errorCode })
  }

  private async checkForCardIssuePointExists(
    errorCode: string,
    ctx: Context,
    tx: ROTransaction,
    id: Id,
    opts?: { withDeleted: boolean },
  ): Promise<CardIssuePoint> {
    if (opts?.withDeleted)
      return (
        (await this.opts.cardIssuePointsRepository.findById(tx, id)) ??
        throwObjectNotFound(`${errorCode} ${this.translate(ctx, d => d.cardIssuePoints.notFoundByIdError, id)}`)
      )
    else
      return (
        (await this.opts.cardIssuePointsRepository.findByIdNonDeleted(tx, id)) ??
        throwObjectNotFound(`${errorCode} ${this.translate(ctx, d => d.cardIssuePoints.notFoundByIdError, id)}`)
      )
  }

  private async checkForUserExists(ctx: Context, tx: ROTransaction, id: Id): Promise<User> {
    return (
      (await this.opts.usersRepository.findByIdNonDeleted(tx, id)) ??
      throwObjectNotFound(`<3296391f> ${this.translate(ctx, d => d.users.notFoundByIdError, id)}`)
    )
  }

  private async checkForMunicipalEntityExists(ctx: Context, tx: ROTransaction, id: Id) {
    ;(await this.opts.municipalEntitiesRepository.findByIdNonDeleted(tx, id)) ??
      throwObjectNotFound(`<6544c0ca> ${this.translate(ctx, d => d.municipalEntities.notFoundByIdError, id)}`)
  }
}

function getFieldsToIndex(cip: CardIssuePoint): (string | undefined)[] {
  return [cip.locality, cip.street, cip.building, cip.comment, cip.number.toString(), cip.phone]
}

export class CardIssuePointsModelEventProcessor implements ModelEventsProcessor {
  supportedEventTypes: string[] = enumGetValues(EventType)

  constructor(
    private opts: {
      cardIssuePointsRepository: CardIssuePointsRepository
      eventCreator: ModelEventCreator
      fullTextSearchService: FullTextSearchService
    },
  ) {}

  // UPDATED
  async createMergeEvent(ctx: Context, tx: ROTransaction, entityId: string): Promise<ModelEvent> {
    const cip =
      (await this.opts.cardIssuePointsRepository.findById(tx, entityId)) ??
      throwObjectNotFound(
        trCore(d => d.cardIssuePoints.notFoundByIdError, entityId),
        { uid: '<3ea8eb24>' },
      )
    return this.opts.eventCreator.create(ctx, tx, {
      entityId,
      mode: CreateEventMode.UPDATE,
      type: EventType.CARD_ISSUE_POINT_MERGE,
      municipalEntityId: cip.municipalEntityId,
      payload: createObj<MergeCardIssuePointPayload>({
        ...cip,
        createdAt: cip.createdAt.toISOString(),
        deletedAt: cip.deletedAt?.toISOString(),
      }),
    })
  }

  async process(ctx: Context, tx: RWTransaction, event: ModelEvent): Promise<boolean> {
    switch (event.type as EventType) {
      case EventType.CARD_ISSUE_POINT_ADD: {
        const p = event.payload as AddCardIssuePointPayload
        const cip: CardIssuePoint = {
          id: event.entityId,
          createdAt: event.createdAt,
          municipalEntityId: p.municipalEntityId,
          number: p.number,
          locality: p.locality,
          street: p.street,
          building: p.building,
          comment: p.comment,
          windowsCount: p.windowsCount,
          openingHours: p.openingHours,
          phone: p.phone,
          responsiblePersonId: p.responsiblePersonId,
        }
        await this.opts.cardIssuePointsRepository.save(tx, cip)
        await this.opts.fullTextSearchService.addToIndex(ctx, tx, event.entityId, getFieldsToIndex(cip))
        return true
      }
      case EventType.CARD_ISSUE_POINT_UPDATE: {
        const p = event.payload as UpdateCardIssuePointPayload
        const cip =
          (await this.opts.cardIssuePointsRepository.findById(tx, event.entityId)) ??
          throwObjectNotFound(
            trCore(d => d.cardIssuePoints.notFoundByIdError, event.entityId),
            { uid: '<f3969344>' },
          )
        const updatedCip: CardIssuePoint = {
          ...cip,
          municipalEntityId: p.municipalEntityId ?? cip.municipalEntityId,
          number: p.number ?? cip.number,
          locality: p.locality ?? cip.locality,
          street: p.street ?? cip.street,
          building: p.building ?? cip.building,
          comment: p.comment ?? cip.comment,
          windowsCount: p.windowsCount ?? cip.windowsCount,
          openingHours: p.openingHours ?? cip.openingHours,
          phone: getUpdatedString(p.phone, cip.phone),
          responsiblePersonId:
            p.responsiblePersonId === null ? undefined : p.responsiblePersonId ?? cip.responsiblePersonId,
        }
        await this.opts.cardIssuePointsRepository.save(tx, updatedCip)

        const fieldsToIndex = getFieldsToIndex(updatedCip)
        if (!isEqual(fieldsToIndex, getFieldsToIndex(cip))) {
          await this.opts.fullTextSearchService.addToIndex(ctx, tx, event.entityId, fieldsToIndex)
        }
        return true
      }
      case EventType.CARD_ISSUE_POINT_DELETE: {
        await this.opts.cardIssuePointsRepository.delete(tx, event.entityId, event.createdAt)
        await this.opts.fullTextSearchService.removeFromIndex(ctx, tx, event.entityId)
        return true
      }
      case EventType.CARD_ISSUE_POINT_MERGE: {
        const p = event.payload as MergeCardIssuePointPayload
        await this.opts.cardIssuePointsRepository.save(tx, {
          ...p,
          id: event.entityId,
          createdAt: new Date(p.createdAt),
          deletedAt: runIfDefined(p.deletedAt, v => new Date(v)),
        })
        return true
      }
    }
    return false
  }
}
